/**
 * Cart drawer - custom element, shared cart queue, line updates, header cart icon.
 * Requires cart-drawer-core.js. Loaded only when settings.cart_action == 'drawer'.
 */
(function () {
  'use strict';

  var Core = window.CartDrawerCore;
  if (!Core || !window.customElements || window.customElements.get('cart-drawer')) return;

  var SECTION = 'cart-drawer';
  // Every cart operation of the drawer goes through this one queue (see the spec, section 5.3a).
  var queue = Core.createQueue();
  // line key -> latest wanted quantity, for changes that have not been sent yet
  var intents = new Map();

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartUrl() {
    return root() + 'cart';
  }

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function extractSection(json) {
    return json && json.sections ? json.sections[SECTION] : null;
  }

  // result: { ok, status, json } from request()
  function readResult(result) {
    return { outcome: Core.classifyResult(result.status, result.json), json: result.json };
  }

  function request(url, init) {
    return Core.timedRequest(function (u, i) { return window.fetch(u, i); }, url, init, Core.REQUEST_TIMEOUT_MS);
  }

  function fetchSection() {
    return request(root() + '?sections=' + SECTION, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (result) {
      if (!result.ok) throw new Error('cart drawer section: HTTP ' + result.status);
      return result.json ? result.json[SECTION] : null;
    });
  }

  function postChange(key, quantity) {
    return request(root() + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: SECTION,
        sections_url: window.location.pathname,
      }),
    }).then(readResult, function () {
      return { outcome: 'unknown', json: null };
    });
  }

  // Line keys contain characters that are awkward in selectors, so compare attributes directly.
  function findLine(el, key) {
    var rows = el.querySelectorAll('[data-line-key]');
    for (var i = 0; i < rows.length; i += 1) {
      if (rows[i].getAttribute('data-line-key') === key) return rows[i];
    }
    return null;
  }

  function showLineError(el, key, message) {
    var row = findLine(el, key);
    var target = row && row.querySelector('[data-line-error]');
    if (target) target.textContent = message || '';
  }

  function afterChange(el, key, result) {
    if (result.outcome !== 'ok') {
      var message = (result.json && (result.json.description || result.json.message)) || el.dataset.errorText || '';
      return el.refresh().then(function () {
        showLineError(el, key, message);
      });
    }
    // A later operation is queued: its response is newer, let it render.
    if (queue.pending > 0) return undefined;
    var shown = el.render(extractSection(result.json)) ? Promise.resolve(true) : el.refresh();
    return shown.then(function () {
      var line = (result.json.items || []).filter(function (item) { return item.key === key; })[0];
      if (result.wanted > 0 && line && line.quantity < result.wanted) {
        showLineError(el, key, (el.dataset.maxText || '').replace('[quantity]', line.quantity));
      }
    });
  }

  function setQuantity(el, key, quantity) {
    var scheduled = intents.has(key);
    intents.set(key, quantity);
    var row = findLine(el, key);
    if (row) row.classList.add('is-updating');
    if (scheduled) return;

    queue.enqueue(function () {
      var wanted = intents.get(key);
      intents.delete(key);
      return postChange(key, wanted).then(function (result) {
        result.wanted = wanted;
        return result;
      });
    }).then(function (result) {
      return afterChange(el, key, result);
    });
  }

  // Resolves once the queue is idle and the handlers of the last operation had their turn.
  function waitUntilSettled() {
    return queue.whenIdle().then(function () {
      return new Promise(function (resolve) { window.setTimeout(resolve, 0); });
    }).then(function () {
      return queue.pending > 0 ? waitUntilSettled() : undefined;
    });
  }

  function focusableIn(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._trigger = null;
      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('keydown', this._onKeydown.bind(this));
      this.addEventListener('submit', this._onSubmit.bind(this));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (trigger) {
        this._trigger = trigger;
      } else if (!this.isOpen) {
        this._trigger = document.activeElement;
      }
      this.classList.add('is-open');
      document.documentElement.classList.add('cart-drawer-open');
      var panel = this.querySelector('.cart-drawer__panel');
      if (panel) panel.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cart-drawer-open');
      var trigger = this._trigger;
      this._trigger = null;
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') trigger.focus();
    }

    // Returns false when html is not a usable drawer; the caller falls back, it never re-adds.
    render(html) {
      if (!html) return false;
      var next = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
      if (!next) return false;

      this.innerHTML = next.innerHTML;
      this.dataset.itemCount = next.dataset.itemCount || '0';
      this.classList.toggle('is-empty', next.classList.contains('is-empty'));

      var count = this.dataset.itemCount;
      Array.prototype.forEach.call(document.querySelectorAll('.cart_count'), function (badge) {
        badge.textContent = count;
      });

      var theme = window.PXUTheme;
      if (theme && theme.currency && theme.currency.show_multiple_currencies
        && window.currencyConverter && typeof window.currencyConverter.convertCurrencies === 'function') {
        window.currencyConverter.convertCurrencies();
      }

      if (this.isOpen) {
        var panel = this.querySelector('.cart-drawer__panel');
        if (panel) panel.focus();
      }
      return true;
    }

    refresh() {
      var el = this;
      return queue.enqueue(fetchSection).then(
        function (html) {
          // A later operation is queued: its response is newer, let it render.
          if (queue.pending > 0) return true;
          return el.render(html);
        },
        function () { return false; }
      );
    }

    refreshAndOpen(trigger) {
      var el = this;
      return this.refresh().then(function (ok) {
        if (ok) el.open(trigger);
        return ok;
      });
    }

    _onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      var row = target.closest('[data-line-key]');
      if (!row) return;
      var key = row.getAttribute('data-line-key');

      if (target.closest('[data-line-remove]')) {
        event.preventDefault();
        setQuantity(this, key, 0);
        return;
      }

      var stepper = target.closest('[data-qty-change]');
      if (stepper) {
        event.preventDefault();
        var input = row.querySelector('[data-qty-input]');
        var current = Core.parseQuantity(input ? input.value : null);
        if (current === null) current = Core.parseQuantity(row.getAttribute('data-quantity')) || 0;
        var next = Math.max(0, current + parseInt(stepper.getAttribute('data-qty-change'), 10));
        if (input) input.value = String(next);
        setQuantity(this, key, next);
      }
    }

    _onChange(event) {
      var input = event.target;
      if (!input || !input.matches || !input.matches('[data-qty-input]')) return;
      var row = input.closest('[data-line-key]');
      if (!row) return;
      var quantity = Core.parseQuantity(input.value);
      if (quantity === null) {
        input.value = row.getAttribute('data-quantity');
        return;
      }
      setQuantity(this, row.getAttribute('data-line-key'), quantity);
    }

    _onKeydown(event) {
      // Enter in a quantity field commits the quantity; it must not submit the form to checkout.
      if (event.key === 'Enter' && event.target && event.target.matches && event.target.matches('[data-qty-input]')) {
        event.preventDefault();
        event.target.blur();
      }
    }

    _onSubmit(event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-cart-drawer-form]')) return;
      if (queue.pending === 0) return;

      event.preventDefault();
      if (this._checkoutWaiting) return;
      this._checkoutWaiting = true;
      var el = this;
      waitUntilSettled().then(function () {
        el._checkoutWaiting = false;
        var current = el.querySelector('[data-cart-drawer-form]');
        if (!current) return;
        var button = current.querySelector('button[data-cart-drawer-checkout]');
        if (button && typeof current.requestSubmit === 'function') {
          current.requestSubmit(button);
          return;
        }
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        current.appendChild(input);
        current.submit();
      });
    }
  }

  var api = {
    queue: queue,
    SECTION: SECTION,
    root: root,
    cartUrl: cartUrl,
    getDrawer: getDrawer,
    extractSection: extractSection,
    readResult: readResult,
    request: request,
    goToCart: function () { window.location.assign(cartUrl()); },
    open: function (trigger) { var el = getDrawer(); if (el) el.open(trigger); },
    close: function () { var el = getDrawer(); if (el) el.close(); },
    refresh: function () { var el = getDrawer(); return el ? el.refresh() : Promise.resolve(false); },
    refreshAndOpen: function (trigger) {
      var el = getDrawer();
      return el ? el.refreshAndOpen(trigger) : Promise.resolve(false);
    },
  };
  window.CartDrawer = api;

  window.customElements.define('cart-drawer', CartDrawer);

  // Header cart icons. Delegated: the sticky nav clones .cart-container after load (sections.js).
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target && event.target.closest ? event.target.closest('.mini_cart') : null;
    if (!link) return;
    var el = getDrawer();
    if (!el) return;
    event.preventDefault();
    el.refreshAndOpen(link).then(function (ok) {
      if (!ok) api.goToCart();
    });
  });

  document.addEventListener('keydown', function (event) {
    var el = getDrawer();
    if (!el || !el.isOpen) return;
    // A fancybox preview on top of the drawer handles its own keys.
    if (document.body.classList.contains('fancybox-active')) return;

    if (event.key === 'Escape') {
      el.close();
      return;
    }
    if (event.key !== 'Tab') return;
    var panel = el.querySelector('.cart-drawer__panel');
    if (!panel) return;
    var items = focusableIn(panel);
    if (!items.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  });

  // Add-ons added outside the queue (product-page.js, personalize flow) announce themselves here.
  document.addEventListener('cart-drawer:refresh', function () {
    var el = getDrawer();
    if (el) el.refresh();
  });

  // Back/forward cache restores the page as it was: resync with the real cart.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var el = getDrawer();
    if (el) el.refresh();
  });
})();
